/**
 * useSettingsForm Hook
 *
 * Manages settings form state with:
 * - Form state initialization from server data with boolean conversion
 * - Change tracking and dirty state detection
 * - Form reset functionality
 * - Utilities for D1/SQLite integer boolean (0/1) conversion
 *
 * Eliminates code duplication from repeated boolean conversion logic
 * and centralizes form state management.
 *
 * @example
 * ```tsx
 * const {
 *   formData,
 *   hasChanges,
 *   updateField,
 *   reset,
 *   toFormDataPayload
 * } = useSettingsForm(settings);
 *
 * // Update a field
 * updateField('timezone', 'UTC-08:00');
 *
 * // Check if form has changes
 * if (hasChanges) {
 *   // Show save button
 * }
 *
 * // Reset to original values
 * reset();
 *
 * // Convert to FormData for submission
 * const formData = toFormDataPayload();
 * ```
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import type {
  PredictionSensitivity,
  AlertConfidenceLevel,
} from '../../constants/velocity-options';

/**
 * Shop settings interface matching database schema
 * Note: Includes legacy fields for DB compatibility.
 * Use SettingsFormState for UI form state.
 */
export interface ShopSettings {
  timezone: string;
  desktop_notifications_enabled: boolean | number;
  weekly_summary_enabled: boolean | number;
  // Weekly Digest Configuration
  weekly_digest_day: number; // 0=Sunday, 6=Saturday
  weekly_digest_time: string; // HH:MM format
  weekly_digest_sections: string; // JSON array of section names
  // Legacy fields - kept in DB, hidden from UI
  notifications_email: boolean | number;
  notifications_slack: boolean | number;
  notifications_admin: boolean | number;
  slack_webhook_url: string | null;
  // Active fields
  email_address: string | null;
  quiet_hours_enabled: boolean | number;
  quiet_hours_start: string;
  quiet_hours_end: string;
  quiet_hours_timezone: string;
  browser_notifications_enabled: boolean | number;
  // Alert Configuration

  // Velocity Configuration
  velocity_lookback_days: 7 | 14 | 30 | 60 | 90;
  prediction_sensitivity: PredictionSensitivity;
  minimum_data_days: 7 | 14 | 21;
  alert_confidence: AlertConfidenceLevel;
  // Legacy fields - kept for migration compatibility
  seasonal_learning_enabled?: boolean | number;
  min_velocity_threshold?: number;
  weighted_average_enabled?: boolean | number;
  velocity_update_frequency?: 'hourly' | 'daily' | 'weekly';
  // Notification Rules
  alert_frequency: 'realtime' | 'digest';
  digest_time: string;
  alert_level_filters: string; // JSON string from DB
  notification_grouping: 'none' | 'product' | 'severity';
  // Data Export Settings
  export_format: 'csv' | 'excel' | 'json';
  auto_export_enabled: boolean | number;
  auto_export_frequency: 'daily' | 'weekly' | 'monthly';
  data_retention_days: 30 | 60 | 90 | 180 | 365;
}

/**
 * Alert level type for notification filtering
 */
export type AlertLevel = 'urgent' | 'warning' | 'info' | 'healthy';

/**
 * Form state with proper boolean types
 * Simplified to exclude legacy notification fields.
 * Channel management is handled separately via notification_channels API.
 */
/**
 * Weekly digest section identifiers
 */
export type WeeklyDigestSection = 'alerts' | 'top_products' | 'velocity_trends' | 'recommendations';

export interface SettingsFormState {
  // Delivery Preferences
  timezone: string;
  weekly_summary_enabled: boolean;
  // Weekly Digest Configuration
  weekly_digest_day: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  weekly_digest_time: string;
  weekly_digest_sections: WeeklyDigestSection[];
  // Other Delivery Settings
  email_address: string;
  quiet_hours_enabled: boolean;
  quiet_hours_start: string;
  quiet_hours_end: string;
  quiet_hours_timezone: string;
  browser_notifications_enabled: boolean;
  // Velocity Configuration
  velocity_lookback_days: 7 | 14 | 30 | 60 | 90;
  minimum_data_days: 7 | 14 | 21;
  // Notification Rules
  alert_frequency: 'realtime' | 'digest';
  digest_time: string;
  alert_level_filters: AlertLevel[];
  notification_grouping: 'none' | 'product' | 'severity';
  // Data Export Settings
  export_format: 'csv' | 'excel' | 'json';
  auto_export_enabled: boolean;
  auto_export_frequency: 'daily' | 'weekly' | 'monthly';
  data_retention_days: 30 | 60 | 90 | 180 | 365;
}

/**
 * Hook return value
 */
export interface UseSettingsFormReturn {
  /** Current form state */
  formData: SettingsFormState;
  /** Whether form has unsaved changes */
  hasChanges: boolean;
  /** Update a single field value */
  updateField: <K extends keyof SettingsFormState>(field: K, value: SettingsFormState[K]) => void;
  /** Update multiple fields at once */
  updateFields: (updates: Partial<SettingsFormState>) => void;
  /** Reset form to original server values */
  reset: () => void;
  /** Convert form data to FormData object for submission */
  toFormDataPayload: () => FormData;
}

/**
 * Convert D1/SQLite integer booleans (0/1) or string booleans to proper boolean
 * D1 (SQLite) stores booleans as integers: 0 = false, 1 = true
 * Form submissions may send string "true"/"false"
 *
 * @param value - Value to convert (number, boolean, or string)
 * @returns Proper boolean value
 */
function normalizeBoolean(value: boolean | number | string | null | undefined): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  if (typeof value === 'string') {
    return value === 'true' || value === '1';
  }
  return false;
}

/**
 * Parse JSON string to array safely
 * @param jsonString - JSON string to parse
 * @param defaultValue - Default value if parsing fails
 * @returns Parsed array or default value
 */
function parseJsonArray<T>(jsonString: string | null | undefined, defaultValue: T[]): T[] {
  if (!jsonString) return defaultValue;
  try {
    const parsed = JSON.parse(jsonString);
    return Array.isArray(parsed) ? parsed : defaultValue;
  } catch {
    return defaultValue;
  }
}

/**
 * Initialize form state from server settings with boolean conversion
 * Only includes active settings fields, excluding legacy notification fields.
 *
 * @param settings - Server settings (may have integer booleans from D1)
 * @returns Normalized form state with proper booleans
 */
function initializeFormState(settings: ShopSettings): SettingsFormState {
  return {
    // Delivery Preferences
    timezone: settings.timezone || 'UTC-05:00',
    weekly_summary_enabled: normalizeBoolean(settings.weekly_summary_enabled),
    // Weekly Digest Configuration
    weekly_digest_day: (settings.weekly_digest_day ?? 1) as 0 | 1 | 2 | 3 | 4 | 5 | 6, // Default Monday
    weekly_digest_time: settings.weekly_digest_time || '09:00',
    weekly_digest_sections: parseJsonArray<WeeklyDigestSection>(
      settings.weekly_digest_sections,
      ['alerts', 'top_products', 'velocity_trends', 'recommendations']
    ),
    // Other Delivery Settings
    email_address: settings.email_address || '',
    quiet_hours_enabled: normalizeBoolean(settings.quiet_hours_enabled),
    quiet_hours_start: settings.quiet_hours_start || '22:00',
    quiet_hours_end: settings.quiet_hours_end || '08:00',
    quiet_hours_timezone: settings.quiet_hours_timezone || 'UTC-05:00',
    browser_notifications_enabled: normalizeBoolean(settings.browser_notifications_enabled),
    // Velocity Configuration
    velocity_lookback_days: settings.velocity_lookback_days || 30,
    minimum_data_days: settings.minimum_data_days || 14,
    // Notification Rules
    alert_frequency: settings.alert_frequency || 'realtime',
    digest_time: settings.digest_time || '09:00',
    alert_level_filters: parseJsonArray<AlertLevel>(
      settings.alert_level_filters,
      ['urgent', 'warning']
    ),
    notification_grouping: settings.notification_grouping || 'none',
    // Data Export Settings
    export_format: settings.export_format || 'csv',
    auto_export_enabled: normalizeBoolean(settings.auto_export_enabled),
    auto_export_frequency: settings.auto_export_frequency || 'weekly',
    data_retention_days: settings.data_retention_days || 90,
  };
}

/**
 * Custom hook for managing settings form state
 */
export function useSettingsForm(initialSettings: ShopSettings): UseSettingsFormReturn {
  // Memoize initial form state to prevent unnecessary re-initialization
  const initialFormState = useMemo(
    () => initializeFormState(initialSettings),
    [initialSettings]
  );

  const [formData, setFormData] = useState<SettingsFormState>(initialFormState);

  // Detect changes by comparing current state to initial state
  const hasChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(initialFormState);
  }, [formData, initialFormState]);

  /**
   * Update form state when server settings change (e.g., after save)
   */
  useEffect(() => {
    setFormData(initializeFormState(initialSettings));
  }, [initialSettings]);

  /**
   * Update a single field value
   */
  const updateField = useCallback(
    <K extends keyof SettingsFormState>(field: K, value: SettingsFormState[K]) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    []
  );

  /**
   * Update multiple fields at once
   */
  const updateFields = useCallback((updates: Partial<SettingsFormState>) => {
    setFormData((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  /**
   * Reset form to original server values
   */
  const reset = useCallback(() => {
    setFormData(initialFormState);
  }, [initialFormState]);

  /**
   * Convert form data to FormData object for Remix form submission
   * Booleans are converted to strings, arrays to JSON strings
   */
  const toFormDataPayload = useCallback(() => {
    const formDataObj = new FormData();

    // Add all fields, converting booleans and arrays appropriately
    Object.entries(formData).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // Arrays need to be serialized as JSON
        formDataObj.append(key, JSON.stringify(value));
      } else {
        formDataObj.append(key, String(value));
      }
    });

    return formDataObj;
  }, [formData]);

  return {
    formData,
    hasChanges,
    updateField,
    updateFields,
    reset,
    toFormDataPayload,
  };
}

/**
 * Email validation utility
 * Basic RFC 5322 compliant email validation
 *
 * @param email - Email address to validate
 * @returns True if email is valid
 */
export function validateEmail(email: string): boolean {
  if (!email) return false;
  // Basic email regex - checks for @ symbol and domain
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate settings form data
 *
 * @param formData - Form data to validate
 * @returns Validation errors object (empty if valid)
 */
export function validateSettingsForm(formData: SettingsFormState): Record<string, string> {
  const errors: Record<string, string> = {};

  // Support email validation (if provided)
  if (formData.email_address && !validateEmail(formData.email_address)) {
    errors.email_address = 'Please enter a valid email address';
  }

  // Quiet hours validation
  if (formData.quiet_hours_enabled) {
    if (!formData.quiet_hours_start) {
      errors.quiet_hours_start = 'Start time is required';
    }
    if (!formData.quiet_hours_end) {
      errors.quiet_hours_end = 'End time is required';
    }
  }

  return errors;
}
